import * as React from 'react';

import * as digitalData from '@ao-internal/digital-data';

import FeedbackFormCarousel from '../components/FeedbackFormCarousel';
import { TaggingEventNames, isReadOnlyMode } from '../globals';
import FeedbackService, { Feedback } from '../services/FeedbackService';
import { useChatDispatch } from '../store';
import useBottomSheetStore from '../store/bottomSheet';

let feedbackServiceInstance: FeedbackService;

// TODO: Convert to enum?
export type FeedbackStageType =
	| 'rating'
	| 'relevanceScore'
	| 'detailScore'
	| 'goalScore'
	| 'comments';

interface UseFeedbackProps {
	saveFeedback: (
		feedbackData: Feedback,
		stage: FeedbackStageType
	) => Promise<void>;
	createFeedbackForm: (feedbackData: Feedback) => void;
}

function fireFeedbackFormCloseBeacon(feedbackData: Feedback) {
	digitalData.sendBeacon(TaggingEventNames.feedbackFormClose, {
		feedbackId: feedbackData.id,
		subjectType: feedbackData.subjectType,
		subjectName: feedbackData.subjectName,
	});
}

export function useFeedback(): UseFeedbackProps {
	const { setMessageFeedback } = useChatDispatch();
	const bottomSheetStore = useBottomSheetStore();

	let isFeedbackSent = true;

	async function saveFeedback(
		feedbackData: Feedback,
		stage: FeedbackStageType
	) {
		try {
			const feedbackId = await feedbackServiceInstance.sendFeedback(
				feedbackData
			);

			feedbackData = {
				...feedbackData,
				id: feedbackId,
			};
		} catch (error) {
			isFeedbackSent = false;
		} finally {
			// Update local state
			setMessageFeedback(feedbackData.messageId, feedbackData);

			digitalData.sendBeacon(TaggingEventNames.feedbackSubmitted, {
				feedbackId: feedbackData.id,
				feedbackStage: stage,
				subjectType: feedbackData.subjectType,
				subjectName: feedbackData.subjectName,
				rating: feedbackData.rating,
				relevanceScore: feedbackData.relevanceScore,
				detailScore: feedbackData.detailScore,
				goalScore: feedbackData.goalScore,
				comments: 'USER_CONTENT',
			});
		}

		if (isFeedbackSent) {
			return Promise.resolve();
		}
		return Promise.reject();
	}

	function createFeedbackForm(feedbackData: Feedback) {
		bottomSheetStore.setOnClose(() => {
			fireFeedbackFormCloseBeacon(feedbackData);
			bottomSheetStore.setIsOpen(false);
		});
		bottomSheetStore.setOptions({
			'aria-labelledby': 'chatbot-feedback-form-title',
			elementToReturnFocusOnClose: document.activeElement
				? (document.activeElement as HTMLElement)
				: undefined,
		});
		bottomSheetStore.setContent(() => (
			<FeedbackFormCarousel
				isReadOnly={isReadOnlyMode}
				feedback={feedbackData}
				onClose={() => {
					fireFeedbackFormCloseBeacon(feedbackData);
					bottomSheetStore.setIsOpen(false);
				}}
			/>
		));
		bottomSheetStore.setIsOpen(true);
	}

	React.useEffect(() => {
		if (!feedbackServiceInstance) {
			// Effectively creates a singleton so we don't instantiate a new instance every time we mount
			// and allows us to mock, so we can spy in our tests
			feedbackServiceInstance = new FeedbackService();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		saveFeedback,
		createFeedbackForm,
	};
}

export default useFeedback;
